import { useMemo } from 'react';

import {
  ACCOUNT_STATUS,
  ACCOUNT_STATUS_DETAIL,
  PAYMENT_GATEWAY,
  SUBSCRIPTION_STATUS,
} from '@adtrackify/at-tracking-event-types';

import { useAccountState } from '@/contexts/account/account-context';
import { useAuthToken } from '@/contexts/auth/auth-context';
import type {
  UseSubscriptionGuardResult,
} from '../types/use-subscription-guard-types';
import {
  ACCOUNT_ACCESS_MODE,
  ACCOUNT_ACCESS_REASON,
} from '../types/use-subscription-guard-enums';



const RECOVERY_STATUSES = new Set<SUBSCRIPTION_STATUS>([
  SUBSCRIPTION_STATUS.PAST_DUE,
  SUBSCRIPTION_STATUS.UNPAID,
]);

function toBillingOnlyResult(
  reason: ACCOUNT_ACCESS_REASON,
  subscriptionStatus: UseSubscriptionGuardResult['subscriptionStatus'],
  isBillingSuspendedForNonpayment: boolean
): UseSubscriptionGuardResult {
  return {
    isLoading: false,
    isCancelled: true,
    isInRecovery: false,
    isBillingSuspendedForNonpayment,
    subscriptionStatus,
    mode: ACCOUNT_ACCESS_MODE.BILLING_ONLY,
    reason,
  };
}

function toFullAccessResult(
  reason: ACCOUNT_ACCESS_REASON,
  subscriptionStatus: UseSubscriptionGuardResult['subscriptionStatus'],
  isBillingSuspendedForNonpayment: boolean
): UseSubscriptionGuardResult {
  return {
    isLoading: false,
    isCancelled: false,
    isInRecovery: reason === ACCOUNT_ACCESS_REASON.IN_RECOVERY,
    isBillingSuspendedForNonpayment,
    subscriptionStatus,
    mode: ACCOUNT_ACCESS_MODE.FULL_ACCESS,
    reason,
  };
}

export function useSubscriptionGuard(): UseSubscriptionGuardResult {
  const token = useAuthToken();
  const {
    currentAccount,
    accountsList,
    hasLoadedAccounts,
    accountsLoadFailed,
  } = useAccountState();

  return useMemo((): UseSubscriptionGuardResult => {
    const isSelectionPending = !currentAccount && accountsList.length > 0;

    // Unified billing gate check: DISABLED + NONPAYMENT
    const isBillingSuspendedForNonpayment =
      currentAccount?.accountStatus === ACCOUNT_STATUS.DISABLED &&
      currentAccount?.accountStatusDetail === ACCOUNT_STATUS_DETAIL.NONPAYMENT;

    const loadingResult: UseSubscriptionGuardResult = {
      isLoading: true,
      isCancelled: false,
      isInRecovery: false,
      isBillingSuspendedForNonpayment: false,
    };

    // Optimistic shell mounts before Cognito sets a token. Hold the access gate
    // in loading so AccessGuard never falls through to RestrictedAccessState.
    if (!token) {
      return loadingResult;
    }

    // Without a cached account, keep the gate loading so we never infer
    // FULL_ACCESS from an empty or failed fetch.
    if (!currentAccount && (!hasLoadedAccounts || accountsLoadFailed || isSelectionPending)) {
      return loadingResult;
    }
    if (!currentAccount) {
      return {
        isLoading: false,
        isCancelled: true,
        isInRecovery: false,
        isBillingSuspendedForNonpayment: false,
        mode: ACCOUNT_ACCESS_MODE.BILLING_ONLY,
        reason: ACCOUNT_ACCESS_REASON.SUBSCRIPTION_MISSING,
      };
    }

    const accountStatus = currentAccount.accountStatus;
    const accountStatusDetail = currentAccount.accountStatusDetail as string | undefined;
    const subscriptionStatus = currentAccount.subscription?.status;
    const paymentGateway = currentAccount.subscription?.paymentGateway;

    let classified: UseSubscriptionGuardResult;

    // PENDING account — hasn't completed billing setup
    if (accountStatus === ACCOUNT_STATUS.PENDING) {
      classified = toBillingOnlyResult(ACCOUNT_ACCESS_REASON.IN_RECOVERY, subscriptionStatus, isBillingSuspendedForNonpayment);
    } else if (accountStatus === ACCOUNT_STATUS.CANCELLED) {
      // CANCELLED account supersedes subscription status
      classified = toBillingOnlyResult(ACCOUNT_ACCESS_REASON.SUBSCRIPTION_CANCELLED, subscriptionStatus, isBillingSuspendedForNonpayment);
    } else if (accountStatus === ACCOUNT_STATUS.DISABLED) {
      // DISABLED account — branch on detail
      let reason: ACCOUNT_ACCESS_REASON;
      if (accountStatusDetail === ACCOUNT_STATUS_DETAIL.NONPAYMENT) {
        reason = ACCOUNT_ACCESS_REASON.BILLING_SUSPENDED_NONPAYMENT;
      } else if (accountStatusDetail === ACCOUNT_STATUS_DETAIL.PAUSED) {
        reason = ACCOUNT_ACCESS_REASON.SUBSCRIPTION_PAUSED;
      } else {
        reason = ACCOUNT_ACCESS_REASON.ACCOUNT_DISABLED_ADMIN;
      }
      classified = toBillingOnlyResult(reason, subscriptionStatus, isBillingSuspendedForNonpayment);
    } else if (subscriptionStatus && RECOVERY_STATUSES.has(subscriptionStatus)) {
      // Subscription in recovery state — gateway fork (T10a)
      if (paymentGateway === PAYMENT_GATEWAY.STRIPE) {
        classified = toFullAccessResult(ACCOUNT_ACCESS_REASON.IN_RECOVERY, subscriptionStatus, isBillingSuspendedForNonpayment);
      } else {
        // Shopify PAST_DUE/UNPAID — no in-app recovery path
        classified = toBillingOnlyResult(ACCOUNT_ACCESS_REASON.SHOPIFY_BILLING_ISSUE, subscriptionStatus, isBillingSuspendedForNonpayment);
      }
    } else if (subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE) {
      // Active subscription — full access
      classified = toFullAccessResult(ACCOUNT_ACCESS_REASON.ACTIVE, subscriptionStatus, isBillingSuspendedForNonpayment);
    } else if (subscriptionStatus === SUBSCRIPTION_STATUS.PENDING) {
      // Pending subscription — payment method required to activate
      classified = toBillingOnlyResult(ACCOUNT_ACCESS_REASON.IN_RECOVERY, subscriptionStatus, isBillingSuspendedForNonpayment);
    } else if (!subscriptionStatus) {
      // No subscription created yet — prompt plan selection
      classified = toBillingOnlyResult(ACCOUNT_ACCESS_REASON.SUBSCRIPTION_MISSING, subscriptionStatus, isBillingSuspendedForNonpayment);
    } else {
      // Everything else (CANCELLED, EXPIRED, FROZEN, DECLINED, NOT_SUBMITTED) — gated
      classified = toBillingOnlyResult(ACCOUNT_ACCESS_REASON.SUBSCRIPTION_CANCELLED, subscriptionStatus, isBillingSuspendedForNonpayment);
    }

    // While accounts revalidate, only trust optimistic FULL_ACCESS from cache.
    // Partial AccountStorage (missing subscription, stale cancelled flags, etc.)
    // must not flash RestrictedAccessState / Manage Billing before the network
    // result arrives.
    if (!hasLoadedAccounts && classified.mode === ACCOUNT_ACCESS_MODE.BILLING_ONLY) {
      return loadingResult;
    }

    return classified;
  }, [token, hasLoadedAccounts, accountsLoadFailed, currentAccount, accountsList.length]);
}
